import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  AddOperandButton,
  ConditionType,
  defaultOperator,
  filterOperatorOptions,
  FilterOption,
  FilterStyledPopper,
  getFilterOptionsWithLegacy,
  getOperator,
  getOperatorColor,
  isNullOperator,
  OpenSelect,
  operatorLabels,
  Select,
  SelectTrigger,
  StringConditionType,
  TextInput,
  useClickOutside,
} from '@fabric-ui/components';
import { Delete } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { noop, renameProp, setNestedValue } from '@fabric-ui/utils';
import { Box, Button, Divider, IconButton, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';

const defaultToEmpty = (getValue: () => any) => {
  try {
    return getValue() || '';
  } catch {
    return '';
  }
};

const FilterInput = ({
  numberField,
  onChange,
  operator,
  value,
  options,
  size = 'small',
  placeholder = 'Type Value',
  width,
  inputWidth,
  disabled = isNullOperator(operator),
  error,
  optionsUseQueryProps,
  skeletonLoading = false,
  endAdornment,
  readOnly,
}: {
  numberField: boolean;
  operator: string;
  value: any;
  onChange: (value: any) => void;
  size?: 'small' | 'medium' | 'xSmall';
  placeholder?: string;
  width?: number;
  inputWidth?: number;
  options: FilterOption[];
  disabled?: boolean;
  error?: any;
  optionsUseQueryProps?: { options: any[]; isLoading: boolean };
  skeletonLoading?: boolean;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
}) => {
  const inputOptions = operator && options.find(({ value: v }) => v === getOperator(operator))?.inputOptions;
  return inputOptions ? (
    <Box sx={{ ...flex.itemsCenter, gap: 1, width }}>
      <TextInput
        sx={{ width: inputWidth, ...inputOptions.sx1 }}
        size={size}
        placeholder={inputOptions.placeholder1 || placeholder}
        value={defaultToEmpty(() => inputOptions.getValue1(value))}
        disableClearButton
        type={numberField ? 'number' : 'text'}
        onChange={newVal => onChange(inputOptions.onChange1(newVal, value))}
        error={error && inputOptions.getValue1(error)}
        inputProps={{ endAdornment, readOnly }}
      />
      {inputOptions.separator}
      <TextInput
        sx={{ width: inputWidth, ...inputOptions.sx2 }}
        size={size}
        placeholder={inputOptions.placeholder2 || placeholder}
        value={defaultToEmpty(() => inputOptions.getValue2(value))}
        disableClearButton
        onChange={newVal => onChange(inputOptions.onChange2(newVal, value))}
        error={error && inputOptions.getValue2(error)}
        inputProps={{ endAdornment, readOnly }}
        type={numberField ? 'number' : 'text'}
      />
    </Box>
  ) : optionsUseQueryProps && [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType) ? (
    <OpenSelect
      style={{ width: inputWidth }}
      size={size}
      options={optionsUseQueryProps.options}
      loading={optionsUseQueryProps.isLoading}
      skeletonLoading={skeletonLoading}
      value={value || null}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      disableClearButton
      isRequired
      optionsUseQueryProps={optionsUseQueryProps}
      readOnly={readOnly}
    />
  ) : (
    <TextInput
      style={{ width: inputWidth }}
      size={size}
      placeholder={placeholder}
      value={value === null || value === undefined || typeof value === 'object' ? '' : value}
      type={numberField ? 'number' : 'text'}
      disableClearButton={numberField}
      disabled={disabled}
      onChange={onChange}
      error={error}
      inputProps={{ endAdornment, readOnly }}
    />
  );
};

const emptyOperand = ['string', 'number', 'IP', 'FIX', 'bool', 'enum'].reduce(
  (obj, key) => ({
    ...obj,
    [key]: { operator: defaultOperator[ConditionType[key]], value: '' },
    [`${key}[]`]: { operator: `${defaultOperator[ConditionType[key]]}|ANY`, value: '' },
  }),
  {}
);

const getDisplayValue = (value: any) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return value.from || value.to ? `${value.from || ''}-${value.to || ''}` : '';
  }
  return value;
};

interface ConditionFilterProps {
  label?: string | ReactNode;
  value: Record<string, any> | null;
  onChange: (value: Record<string, any> | null) => void;
  onClose?: () => void;
  onOpen?: () => void;
  setRef?: (ref: any) => void;
  size?: 'small' | 'xSmall' | 'medium';
  disabled?: boolean;
  trigger?: any;
  fieldType: string; // e.g., 'string', 'number', 'string[]'
  isMultipleCondition?: boolean;
  selectIcon?: ReactNode;
  defaultOperator?: string;
  operatorOptions?: FilterOption[];
}

export const ConditionFilter: React.FC<ConditionFilterProps> = ({
  onChange,
  value,
  label = '',
  size = 'small',
  disabled,
  trigger,
  fieldType,
  isMultipleCondition = true,
  onOpen = noop,
  onClose = noop,
  setRef = noop,
  selectIcon,
  defaultOperator: defaultOperatorFromProps,
  operatorOptions,
}) => {
  const { palette } = useTheme();
  const isNumberField = fieldType === 'number';
  const initialFilter = { ...(emptyOperand[fieldType] ?? {}), operator: defaultOperatorFromProps || emptyOperand[fieldType]?.operator };
  const currentOperators = operatorOptions || filterOperatorOptions[fieldType];

  const [conditionValue, setConditionValue] = useState(value || { [isNumberField ? 'or' : 'and']: [initialFilter] });
  const operator = Object.keys(conditionValue)[0] as 'and' | 'or';
  const valueOperator = value ? Object.keys(value)[0] : null;

  const buttonRef = useRef(null);
  const popperInnerRef = useRef(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setConditionValue(value || { [isNumberField ? 'or' : 'and']: [initialFilter] });
  }, [value, isNumberField, JSON.stringify(initialFilter)]);

  const handleSelectConditionChange = (newValue: unknown, index: number) => {
    setErrors([]);
    setConditionValue(prev => {
      const nestedPrev = prev[operator][index];
      const updatedValue = isNullOperator(newValue as string) ? '' : nestedPrev.value;
      return setNestedValue(prev, `${operator}.${index}`, { operator: newValue, value: updatedValue });
    });
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  useClickOutside(popperInnerRef, handleClose);

  const tooltipValue = useMemo(
    () =>
      value && valueOperator
        ? value[valueOperator]
            .map(
              (operand, index, arr) =>
                `${operatorLabels[operand.operator]} ${getDisplayValue(operand.value)}${index === arr.length - 1 ? '' : ` ${valueOperator.toUpperCase()} `}`
            )
            .join('')
        : '',
    [value, valueOperator]
  );

  const displayValue = useMemo(
    () =>
      value && valueOperator
        ? `${operatorLabels[value[valueOperator][0].operator]}  ${getDisplayValue(value[valueOperator][0].value)}`
        : '',
    [value, valueOperator]
  );

  const triggerProps = {
    ref: buttonRef,
    hasValidValue: !!value,
    displayValue,
    tooltipValue,
    chipCount: value && value[operator] ? value[operator].length - 1 : undefined,
    open,
    label: label as React.ReactNode,
    size: size as 'small' | 'xSmall' | 'medium',
    disabled,
    selectIcon,
    onClick: () => {
      setOpen(true);
      onOpen();
    },
  } as const;

  // @ts-ignore
  // @ts-ignore
  return (
    <span ref={setRef}>
      {trigger ? React.cloneElement(trigger, triggerProps) : <SelectTrigger {...triggerProps} />}
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        sx={{ minWidth: '400px', width: 'fit-content', padding: 0 }}>
        <Box style={{ display: open ? 'block' : 'none' }} ref={popperInnerRef}>
          <Box sx={{ gap: 1, p: 3, maxHeight: 380, overflowY: 'auto' }}>
            {initialFilter &&
              conditionValue[operator].map((operand, index, operands) => (
                <Box key={operand}>
                  <Box
                    sx={{
                      ...flex.itemsCenter,
                      p: '12px',
                      border: `1px solid ${palette.colors.neutrals[300]}`,
                      gap: 1,
                      borderRadius: '6px',
                      // @ts-ignore
                      '.MuiPaper-root': theme => ({ ...(theme?.components?.MuiAutocomplete?.styleOverrides?.paper || {}) }),
                    }}>
                    <Select
                      size="small"
                      style={{ width: 170, input: { fontWeight: 600 } }}
                      isRequired
                      muiProps={{ disablePortal: true }}
                      placeholder="Select Operator"
                      value={getOperator(operand.operator)}
                      onChange={val => handleSelectConditionChange(val, index)}
                      options={getFilterOptionsWithLegacy(currentOperators, operand.operator)}
                    />
                    <FilterInput
                      value={operand.value}
                      operator={operand.operator}
                      numberField={isNumberField}
                      inputWidth={210}
                      onChange={val => {
                        setErrors([]);
                        setConditionValue(prev => setNestedValue(prev, `${operator}.${index}.value`, val));
                      }}
                      options={currentOperators}
                      error={errors[index]?.value}
                    />
                    <IconButton
                      disabled={operands.length === 1 && conditionValue[operator][index].value === ''}
                      onClick={() => {
                        if (operands.length === 1) {
                          setConditionValue(prev => setNestedValue(prev, `${operator}.${index}.value`, ''));
                          onChange(null);
                        } else {
                          setConditionValue(prev => ({ [operator]: prev[operator].filter((o, i) => i !== index) }));
                        }
                        setErrors([]);
                      }}>
                      <Delete />
                    </IconButton>
                  </Box>
                  <Box sx={{ ...flex.center, py: 1 }}>
                    {operands.length > 1 && index !== operands.length - 1 && (
                      <ToggleButtonGroup
                        color="white"
                        value={operator}
                        onChange={() => setConditionValue(prev => renameProp(operator, operator === 'and' ? 'or' : 'and', prev))}
                        size="xSmall"
                        exclusive>
                        <ToggleButton value="and">
                          <Box
                            sx={theme => ({
                              fontWeight: 600,
                              fontSize: 13,
                              color: operator === 'and' ? getOperatorColor(theme, 'and') : 'inherit',
                            })}>
                            AND
                          </Box>
                        </ToggleButton>
                        <ToggleButton value="or">
                          <Box
                            sx={theme => ({
                              fontWeight: 600,
                              fontSize: 13,
                              color: operator === 'or' ? getOperatorColor(theme, 'or') : 'inherit',
                            })}>
                            OR
                          </Box>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Box>
                </Box>
              ))}
            {isMultipleCondition && (
              <Box sx={flex.center}>
                <AddOperandButton
                  operator={operator}
                  isAdd
                  onClick={() => setConditionValue(prev => ({ [operator]: [...prev[operator], initialFilter] }))}
                />
              </Box>
            )}
          </Box>
          <Divider orientation="horizontal" />
          <Box sx={{ p: 2, ...flex.justifyBetweenCenter }}>
            <Button
              onClick={() => {
                setErrors([]);
                setConditionValue({ [operator]: [initialFilter] });
                onChange(null);
              }}
              size="small">
              Reset
            </Button>
            <div>
              <Button onClick={handleClose} size="small">
                Close
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const getErrors = (val: any, op: string) =>
                    !op ? ['Select Operator'] : currentOperators.find(({ value: v }) => v === op)?.getErrors?.(val);

                  const validOperands = conditionValue[operator].filter(
                    ({ value: v, operator: op }) => typeof v === 'number' || isNullOperator(op) || !!v
                  );

                  if (!validOperands.length) {
                    setConditionValue({ [operator]: [initialFilter] });
                    onChange(null);
                    handleClose();
                  } else {
                    const errorsExp = validOperands.map(({ value: v, operator: op }) => ({ operator: op, value: getErrors(v, op) }));
                    if (errorsExp.some(({ value: v }) => v)) {
                      setErrors(errorsExp);
                    } else {
                      const nextConditionValue = { [operator]: validOperands };
                      setConditionValue(nextConditionValue);
                      onChange(nextConditionValue);
                      handleClose();
                    }
                  }
                }}>
                Apply
              </Button>
            </div>
          </Box>
        </Box>
      </FilterStyledPopper>
    </span>
  );
};

export default ConditionFilter;
